// AI-CONTEXT: See docs/features/arbeidsvaardigheden.md
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'
import { db } from '../../firebase/config'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import { client } from '../../hooks/Client'
import EffectMatrix from '../../components/personas/arbeidsvaardigheden/EffectMatrix'
import GroeiPerThema from '../../components/personas/arbeidsvaardigheden/GroeiPerThema'
import { Auth } from '../../StateManagment/Auth'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { EFFECTEN } from '../../helpers/arbeidsvaardighedenHelpers/arbeidsvaardighedenStandaard'
import berekenEffectscores from '../../helpers/arbeidsvaardighedenHelpers/berekenEffectscores'
import beoordelingStatus from '../../helpers/arbeidsvaardighedenHelpers/beoordelingStatus'
import { groeiRijenVoorWeergave, groeiToelichting, datumUI } from '../../helpers/arbeidsvaardighedenHelpers/groeiWeergave'
import { nieuweBeoordeling, antwoordUpdate, datumUpdate, dagSleutel, gesorteerd, isBewerkbaar } from '../../helpers/arbeidsvaardighedenHelpers/beoordelingDocument'

const COLLECTIE = 'WorkSkillsAssessments'

// Staat er geen beoordeling open, dan is er geen antwoordenmap. Een verse `{}`
// per render zou de useMemo hieronder elke keer laten herrekenen; als vaste
// referentie doet hij dat niet. AI-CONTEXT: docs/pitfalls.md#state-useeffect-inline-filter
const GEEN_ANTWOORDEN = {}

/**
 * @param {object} props
 * @param {'geen'|'actueel'|'verouderd'} props.status - Uitkomst van beoordelingStatus
 * @param {number|null} props.maandenGeleden - Leeftijd van de laatste beoordeling
 * @param {boolean} props.geladen - False zolang de beoordelingen nog niet gelezen zijn
 */
const StatusPil = ({ status, maandenGeleden, geladen }) => {
    // 'Nog niet beoordeeld' is een bewering over deze deelnemer. Zolang de read
    // loopt (of geweigerd wordt) is die onwaar, en juist dan is hij het meest
    // misleidend: hij ziet er precies zo uit als een echte lege stand.
    if (!geladen) return null
    if (status === 'geen') return <span className="av-status geen">Nog niet beoordeeld</span>
    const tekst = maandenGeleden === 0 ? 'deze maand' : `${maandenGeleden} mnd geleden`
    return <span className={`av-status ${status}`}>{tekst}</span>
}

/**
 * Persona-subtab: de beoordeling arbeidsvaardigheden volgens MSI
 * Arbeidsvaardigheden. De begeleider oordeelt over de deelnemer op 46
 * tegenpolenstellingen; elke beoordeling is een eigen meting met datum.
 *
 * @param {object} props
 * @param {string} props.personaNavigation - Actieve subtab; dit tabblad rendert verborgen tenzij 'arbeidsvaardigheden'
 * @param {object} props.item - Het persona-document (met `ID`)
 */
const Arbeidsvaardigheden = ({ personaNavigation, item }) => {
    const [auth] = useContext(Auth)
    const [, setSaved, , setSavedMessage] = useContext(SavedIcon)

    const beoordelingen = useFirestoreGeneral(COLLECTIE, 'PersonaID', item.ID)
    const [geselecteerdId, setGeselecteerdId] = useState(null)

    // De hook geeft '' terug vóór de eerste snapshot, en blijft daarop staan als
    // de read geweigerd wordt. 'Nog geen beoordeling' zou dan een onwaarheid zijn
    // die er identiek uitziet als de echte lege stand.
    const geladen = Array.isArray(beoordelingen)

    const lijst = useMemo(() => gesorteerd(beoordelingen || []), [beoordelingen])
    const laatste = lijst[0] || null

    // Geen terugval op de nieuwste: het tabblad opent geen beoordeling uit
    // zichzelf. Wie het tabblad opent ziet eerst het totalenoverzicht en de rij
    // beoordelingen; pas een klik zet er een open.
    const huidige = geselecteerdId ? lijst.find((b) => b.ID === geselecteerdId) || null : null

    // Alleen de ANTWOORDEN gaan op slot. De nieuwste is altijd bewerkbaar, en een
    // eerdere zolang hij nooit is afgerond - anders kan de begeleider werk dat
    // hij halverwege liet liggen niet meer afmaken.
    //
    // De datum valt hier bewust buiten: die is te corrigeren zolang de
    // beoordeling open staat, ook bij een afgeronde eerdere. Een jaartal dat bij
    // het invullen misging (2024 in plaats van 2026) was anders nergens meer
    // recht te zetten - ook niet met een nieuwe beoordeling, want die vervangt
    // de foute niet. Zie docs/inbox.md #277.
    const antwoordenBewerkbaar = isBewerkbaar(huidige, laatste)

    const antwoorden = huidige?.Antwoorden || GEEN_ANTWOORDEN
    const rijen = useMemo(() => berekenEffectscores(antwoorden), [antwoorden])
    const rijVoor = (id) => rijen.find((r) => r.id === id)

    const status = beoordelingStatus(laatste?.Datum ? new Date(laatste.Datum) : null)

    // Klikken naast de open beoordeling sluit hem. De ref omvat ook de rij met
    // knoppen, zodat het wisselen van beoordeling niet eerst als 'ernaast' telt.
    // Op mousedown en niet op click: een klik die buiten begint en binnen
    // eindigt (tekst selecteren) hoort niet te sluiten, en andersom moet het
    // sluiten al gebeurd zijn voordat een onderliggende knop zijn beurt krijgt.
    const paneelRef = useRef(null)
    useEffect(() => {
        if (!geselecteerdId) return undefined
        const buitenKlik = (e) => {
            if (paneelRef.current && !paneelRef.current.contains(e.target)) setGeselecteerdId(null)
        }
        document.addEventListener('mousedown', buitenKlik)
        return () => document.removeEventListener('mousedown', buitenKlik)
    }, [geselecteerdId])

    // Groei is de laatste beoordeling min de eerste, per thema - over de hele
    // reeks, niet over de beoordeling die nu open staat. Pas vanaf twee
    // beoordelingen valt er iets over verloop te zeggen.
    const groeiRijen = useMemo(() => groeiRijenVoorWeergave(lijst), [lijst])


    const startBeoordelingHandler = async () => {
        const id = uuid()
        await db.collection(COLLECTIE).add(
            nieuweBeoordeling({
                id,
                client,
                personaId: item.ID,
                authId: auth?.ID,
                naam: [auth?.ForName, auth?.SurName].filter(Boolean).join(' '),
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
        )
        setGeselecteerdId(id)
        setSaved('flex')
    }

    // De begeleider vult een beoordeling vaak in nadat het gesprek is geweest, of
    // werkt een papieren formulier bij. Zonder dit veld staat elke beoordeling op
    // de dag waarop het formulier is aangemaakt, en dat is niet de meetdatum.
    const datumHandler = (e) => {
        // De waarde eerst uitlezen: e.target is het levende element, niet de
        // waarde. AI-CONTEXT: docs/pitfalls.md#state-updater-reads-event-target-too-late
        const waarde = e.target.value
        if (!huidige?.docid) return

        const patch = datumUpdate(waarde)
        if (!patch) {
            // Stil weigeren zou alleen het veld laten terugspringen, zonder dat
            // iemand begrijpt waarom.
            setSavedMessage('Kies een bestaande datum die niet in de toekomst ligt')
            setSaved('flex')
            return
        }

        db.collection(COLLECTIE)
            .doc(huidige.docid)
            .update({ ...patch, Timestamp: firebase.firestore.FieldValue.serverTimestamp() })
            .then(() => setSaved('flex'))
            .catch((err) => console.error('datum niet opgeslagen:', err))
    }

    const antwoordHandler = (effectId) => (nummer, waarde) => {
        if (!huidige?.docid) return
        db.collection(COLLECTIE)
            .doc(huidige.docid)
            .update({
                ...antwoordUpdate({ antwoorden, effectId, nummer, waarde }),
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
            .then(() => setSaved('flex'))
            .catch((err) => console.error('antwoord niet opgeslagen:', err))
    }

    return (
        <div className={`av-paneel${personaNavigation === 'arbeidsvaardigheden' ? '' : ' is-hidden'}`}>
            <div className="av-container">
                <header className="av-kop">
                    <StatusPil status={status.status} maandenGeleden={status.maandenGeleden} geladen={geladen} />
                </header>

                {/* Eerst het totaal over alle beoordelingen heen, dan pas de
                    losse beoordelingen: de vraag 'hoe gaat het met deze
                    deelnemer' gaat voor op 'welke formulieren zijn er'. */}
                {lijst.length > 1 && (
                    <GroeiPerThema
                        titel="Groei per thema"
                        toelichting={groeiToelichting(lijst)}
                        rijen={groeiRijen}
                        eenheid="punt"
                    />
                )}

                <div className="av-metingen-balk" ref={paneelRef}>
                    <nav className="av-metingen" aria-label="Beoordelingen">
                        {lijst.map((b) => (
                            <button
                                key={b.ID}
                                type="button"
                                className={huidige && b.ID === huidige.ID ? 'av-meting actief' : 'av-meting'}
                                onClick={() => setGeselecteerdId(b.ID)}
                            >
                                <span className="av-meting-datum">{datumUI(b.Datum)}</span>
                                <span className="av-meting-meta">
                                    {b.Compleet ? 'compleet' : 'onaf'}
                                    {b.BeoordeeldDoorNaam ? ` · ${b.BeoordeeldDoorNaam}` : ''}
                                </span>
                            </button>
                        ))}
                    </nav>

                    <button type="button" className="button-simple av-nieuw" onClick={startBeoordelingHandler}>
                        Nieuwe beoordeling
                    </button>

                    {huidige && (
                        <div className="av-beoordeling">
                            <div className="av-meting-kop">
                                <span>
                                    <label className="av-datum-veld">
                                        Datum beoordeling
                                        <input
                                            type="date"
                                            value={huidige.Datum || ''}
                                            max={dagSleutel(new Date())}
                                            onChange={datumHandler}
                                        />
                                    </label>
                                    {huidige.BeoordeeldDoorNaam ? ` · ${huidige.BeoordeeldDoorNaam}` : ''}
                                </span>
                                {/* Een AFGERONDE eerdere meting is een vastgelegd oordeel op
                                    een moment. Wie hem achteraf bijwerkt verandert de nulmeting
                                    waartegen alle latere metingen worden gelezen, zonder dat
                                    dat ergens zichtbaar wordt. Een onafgeronde legde niets
                                    vast en mag alsnog af.

                                    'Alleen lezen' staat er niet meer: het datumveld hiernaast
                                    is wel te bedienen, en twee tekens die elkaar tegenspreken
                                    zijn erger dan geen teken.
                                    AI-CONTEXT: docs/pitfalls.md#ui-independent-badges-contradict-each-other */}
                                {!antwoordenBewerkbaar &&
                                    <span className="av-alleenlezen">antwoorden liggen vast · afgerond</span>
                                }
                            </div>

                            {EFFECTEN.map((effect) => {
                                const rij = rijVoor(effect.id)
                                return (
                                    <EffectMatrix
                                        key={effect.id}
                                        effect={effect}
                                        antwoorden={antwoorden[effect.id] || {}}
                                        score={rij?.score ?? null}
                                        beantwoord={rij?.beantwoord ?? 0}
                                        totaal={rij?.totaal ?? 0}
                                        readOnly={!antwoordenBewerkbaar}
                                        onAntwoord={antwoordHandler(effect.id)}
                                    />
                                )
                            })}
                        </div>
                    )}
                </div>

                {!geladen && <p className="av-leeg">Laden…</p>}
                {geladen && lijst.length === 0 && <p className="av-leeg">Nog geen beoordeling.</p>}
            </div>
        </div>
    )
}

export default Arbeidsvaardigheden
